import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { translations } from "./i18n";
import { Footer } from "./components/layout/Footer";
import { Header } from "./components/layout/Header";
import { Seo } from "./components/Seo";
import { AdvantagesSection } from "./components/sections/AdvantagesSection";
import { AvailableSection } from "./components/sections/AvailableSection";
// import { BreakSection } from "./components/sections/BreakSection";
import { ContactSection } from "./components/sections/ContactSection";
import { FaqSection } from "./components/sections/FaqSection";
import { Hero } from "./components/sections/Hero";
import { HowSection } from "./components/sections/HowSection";
import { ProblemInsightSection } from "./components/sections/ProblemInsightSection";
// import { ProblemSection } from "./components/sections/ProblemSection";
// import { RiskSection } from "./components/sections/RiskSection";
import { SolutionSection } from "./components/sections/SolutionSection";
import { UseCasesSection } from "./components/sections/UseCasesSection";
import { useReveal } from "./hooks/useReveal";
import { AboutPage } from "./pages/AboutPage";
import { DisclaimerPage } from "./pages/DisclaimerPage";
import { ImprintPage } from "./pages/ImprintPage";
import { PricingPage } from "./pages/PricingPage";
import { PrivacyPage } from "./pages/PrivacyPage";

const getSystemTheme = () => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

function AppContent() {
  const [themePreference, setThemePreference] = useState(() => {
    if (typeof window === "undefined") return "system";
    const saved = localStorage.getItem("themePreference");
    return saved === "light" || saved === "dark" ? saved : "system";
  });
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "de";
    return localStorage.getItem("lang") || "de";
  });
  const location = useLocation();
  const theme = themePreference === "system" ? systemTheme : themePreference;
  const indexablePaths = new Set([
    "/", "/about", "/disclaimer", "/imprint", "/pricing", "/privacy",
  ]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event) => setSystemTheme(event.matches ? "dark" : "light");

    setSystemTheme(media.matches ? "dark" : "light");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    if (theme === "dark") html.classList.add("dark");
    else html.classList.remove("dark");
    localStorage.setItem("themePreference", themePreference);
    localStorage.removeItem("theme");
    html.style.setProperty(
      "--logo-filter",
      theme === "dark" ? "invert(1) brightness(1.2)" : "none"
    );
  }, [theme, themePreference]);

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useReveal(location.pathname);

  useEffect(() => {
    if (!location.hash) return;

    let cancelled = false;
    let retryTimer;
    const targetId = decodeURIComponent(location.hash.slice(1));

    const scrollToTarget = () => {
      if (cancelled) return;

      const target = document.getElementById(targetId);
      if (!target) {
        retryTimer = window.setTimeout(scrollToTarget, 50);
        return;
      }

      const headerOffset = 84;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    };

    const frame = window.requestAnimationFrame(scrollToTarget);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(retryTimer);
    };
  }, [location.pathname, location.hash]);

  const t = translations[lang];
  const isHome = location.pathname === "/";
  const reloadWithPreference = (key, value) => {
    localStorage.setItem(key, value);
    window.location.reload();
  };
  const handleThemeChange = (nextTheme) => {
    if (nextTheme === themePreference) return;
    setThemePreference(nextTheme);
    reloadWithPreference("themePreference", nextTheme);
  };
  const handleLangChange = (nextLang) => {
    if (nextLang === lang) return;
    setLang(nextLang);
    reloadWithPreference("lang", nextLang);
  };

  return (
    <div className="grain min-h-screen relative">
      <Seo pathname={location.pathname} noindex={!indexablePaths.has(location.pathname)} />
      <Header
        theme={theme}
        setTheme={handleThemeChange}
        lang={lang}
        setLang={handleLangChange}
        t={t}
        isHome={isHome}
      />
      <Routes>
        <Route path="/" element={<HomePage t={t} theme={theme} />} />
        <Route path="/about" element={<AboutPage t={t} />} />
        <Route path="/disclaimer" element={<DisclaimerPage t={t} />} />
        <Route path="/imprint" element={<ImprintPage t={t} />} />
        <Route path="/pricing" element={<PricingPage t={t} />} />
        <Route path="/privacy" element={<PrivacyPage t={t} />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer t={t} />
    </div>
  );
}

function NotFoundPage() {
  return (
    <main className="container-x flex min-h-[70vh] flex-col items-center justify-center py-32 text-center">
      <p className="eyebrow mb-4">404</p>
      <h1 className="font-display text-4xl font-medium">Seite nicht gefunden</h1>
      <p className="mt-5 max-w-xl text-muted-foreground">
        Die angeforderte Seite existiert nicht oder wurde verschoben.
      </p>
      <a href="/" className="btn-pill btn-primary mt-8">Zur Startseite</a>
    </main>
  );
}

function HomePage({ t, theme }) {
  return (
    <main>
      <Hero t={t} />
      <ProblemInsightSection t={t} />
      {/* <ProblemSection t={t} /> */}
      {/* <RiskSection t={t} /> */}
      {/* <BreakSection t={t} /> */}
      <SolutionSection t={t} />
      <HowSection t={t} />
      <AdvantagesSection t={t} />
      <AvailableSection t={t} theme={theme} />
      <UseCasesSection t={t} />
      <FaqSection t={t} />
      <ContactSection t={t} />
    </main>
  );
}

export default App;
